<template>
    <div class="create-product">

        <b-modal id="remove-product-modal" centered size="md">
                Are you sure you want to delete the selected product? This action can not be undone.

            <template #modal-footer="{ cancel, ok }">
                <b-button @click="cancel">Cancel</b-button>
                <b-button @click="deleteProduct(); ok()" variant="danger">Ok</b-button>
            </template>
        </b-modal>


        <b-modal id="product-modal"
                 :title="editMode ? (editProduct.id ? 'Edit Product' : 'Create new product') : 'View product'" centered
                 size="xl"
                 @cancel="onCancelProductCreation">
            <b-form>

                <div class="row">
                    <div class="col-6">
                        <template v-if="editMode">
                            <b-form-group label="Tuotenimi / Product name" label-for="product-name">
                                <b-form-input
                                    id="product-name"
                                    v-model="editProduct.name"
                                    :state="errors.name === null"
                                    @input="errors.name = null"
                                    placeholder="Enter product name"
                                    required
                                ></b-form-input>

                                <template v-if="errors.name !== null">
                                    <b-form-invalid-feedback v-for="(error, key) in errors.name" :key="'name_error_' + key" force-show>
                                        {{ error }}
                                    </b-form-invalid-feedback>
                                </template>

                            </b-form-group>
                        </template>
                        <template v-else>
                            <label class="d-block productLabel">Tuotenimi / Product name</label>
                            <p class="productDetail"> {{ editProduct.name }}</p>
                        </template>

                    </div>
                    <div class="col-6">
                        <template v-if="editMode">
                            <b-form-group label="Valmistaja / Manufacturer" label-for="product-manufacturer">
                                <b-form-input
                                    id="product-manufacturer"
                                    :state="errors.manufacturer === null"
                                    @input="errors.manufacturer = null"
                                    v-model="editProduct.manufacturer"
                                    placeholder="Enter product manufacturer"
                                    required
                                ></b-form-input>

                                <template v-if="errors.manufacturer !== null">
                                    <b-form-invalid-feedback v-for="(error, key) in errors.manufacturer" :key="'manufacturer_error_' + key" force-show>
                                        {{ error }}
                                    </b-form-invalid-feedback>
                                </template>
                            </b-form-group>
                        </template>
                        <template v-else>
                            <label class="d-block productLabel">Valmistaja / Manufacturer</label>
                            <p class="productDetail"> {{ editProduct.manufacturer }}</p>
                        </template>
                    </div>
                </div>

                <div class="row">
                    <div class="col-6">
                        <template v-if="editMode">
                            <b-form-group label="Tuote kategoria / Product category" label-for="product-category">
                                <b-form-select
                                    :state="errors['category.id'] === null"
                                    v-model="editProduct.product_category_id"
                                    :options="categories"
                                    required
                                ></b-form-select>

                                <template v-if="errors['category.id'] !== null">
                                    <b-form-invalid-feedback v-for="(error, key) in errors['category.id']" :key="'category_error_' + key" force-show>
                                        {{ error }}
                                    </b-form-invalid-feedback>
                                </template>
                            </b-form-group>
                        </template>
                        <template v-else>
                            <label class="d-block productLabel">Tuote kategoria / Product category</label>
                            <p class="productDetail"> {{ editProduct.category ? editProduct.category.name : '-' }}</p>
                        </template>
                    </div>
                    <div class="col-6">
                        <template v-if="editMode">
                            <b-form-group label="ETA/CE" label-for="product-eta_ce">
                                <b-form-input
                                    id="product-eta_ce"
                                    v-model="editProduct.eta_ce"
                                    :state="errors.eta_ce === null"
                                    placeholder="ETA/CE"
                                    required
                                    @input="errors.eta_ce = null"
                                ></b-form-input>

                                <template v-if="errors.eta_ce !== null">
                                    <b-form-invalid-feedback v-for="(error, key) in errors.eta_ce" :key="'eta_ce_error_' + key" force-show>
                                        {{ error }}
                                    </b-form-invalid-feedback>
                                </template>
                            </b-form-group>
                        </template>
                        <template v-else-if="editProduct.eta_ce">
                            <label class="d-block productLabel">ETA/CE</label>
                            <p class="productDetail"> {{ editProduct.eta_ce }}</p>
                        </template>
                    </div>
                </div>

                <hr/>

                <template v-if="editMode || (editProduct.variants.fields && editProduct.variants.items.length > 0)">
                    <label class="d-block productLabel">Variants</label>

                    <div v-if="editMode" class="table-responsive">
                        <table v-if="editProduct.variants && editProduct.variants.fields.length > 0"
                               class="table b-table table-bordered border small">
                            <thead>
                            <tr>
                                <th></th>
                                <th v-for="(columnHeader, headerIndex) in editProduct.variants.fields" :key="'columnHeader_' + headerIndex">
                                    <b-button class="d-inline-block" size="small" type="button"
                                              @click="deleteProductVariantHeaderValue(headerIndex)"
                                              v-b-tooltip.lefttop="'Delete Column'"
                                    >
                                        <b-icon-x-circle-fill variant="danger"></b-icon-x-circle-fill>
                                    </b-button>
                                    <b-form-input
                                        :value="editProduct.variants.fields[headerIndex]"
                                        @input="updateProductVariantHeaderValue(headerIndex, $event)"
                                        class="d-inline-block"
                                        :state="!!editProduct.variants.fields[headerIndex]"
                                        placeholder="Enter column name"
                                        required
                                    ></b-form-input>

                                </th>
                            </tr>
                            </thead>
                            <tbody v-if="editProduct.variants.items.length > 0">
                            <tr v-for="(item, itemIndex) in editProduct.variants.items" :key="'variant_' + itemIndex">
                                <td>
                                    <b-button class="d-inline-block" size="small" type="button"
                                              v-b-tooltip.lefttop="'Delete Row'"
                                              @click="editProduct.variants.items.splice(itemIndex, 1)">
                                        <b-icon-x-circle-fill variant="danger"></b-icon-x-circle-fill>
                                    </b-button>
                                </td>
                                <td v-for="columnHeader in editProduct.variants.fields" :key="'variant_' + columnHeader">
                                    <b-form-input
                                        :value="editProduct.variants.items[itemIndex][columnHeader]"
                                        @change="updateProductVariantItemValue(itemIndex, columnHeader, $event)"
                                        placeholder="Enter column value"
                                        :state="!!editProduct.variants.items[itemIndex][columnHeader]"
                                        required
                                    ></b-form-input>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <b-table
                        class="variantsTable"
                        v-else-if="editProduct.variants && editProduct.variants.items && editProduct.variants.items.length > 0"
                        :fields="editProduct.variants.fields" :items="editProduct.variants.items" bordered
                        outlined
                        responsive/>

                    <b-button-group v-if="editMode" align="right">
                        <b-button type="button" @click="addProductVariantItem">
                            <b-icon-plus-circle-fill variant="danger"></b-icon-plus-circle-fill>
                            Add row
                        </b-button>
                        <b-button type="button" @click="editProduct.variants.fields.push('')">
                            <b-icon-plus-circle-fill variant="danger"></b-icon-plus-circle-fill>
                            Add column
                        </b-button>
                    </b-button-group>
                    <div class="clearfix"></div>
                    <hr/>

                </template>


                <template v-if="editMode">
                    <b-form-group label="Description" label-for="product-description">
                        <b-form-textarea
                            id="product-description"
                            v-model="editProduct.description"
                            :state="errors.description === null"
                            placeholder="Enter product description"
                            required
                            rows="5"
                            @input="errors.description = null"
                        ></b-form-textarea>

                        <template v-if="errors.description !== null">
                            <b-form-invalid-feedback v-for="(error, key) in errors.description" :key="'description_error_' + key" force-show>
                                {{ error }}
                            </b-form-invalid-feedback>
                        </template>
                    </b-form-group>
                </template>
                <template v-else>
                    <label class="d-block productLabel">Description</label>
                    <p class="productDetail"> {{ editProduct.description }}</p>
                </template>

                <template v-if="editMode">
                    <b-form-group label="Link (use ; for multiple)" label-for="product-link">
                        <b-form-input
                            id="product-link"
                            v-model="editProduct.link"
                            :state="errors.link === null"
                            placeholder="Enter product link"
                            type="text"
                            @input="errors.link = null"
                        ></b-form-input>

                        <template v-if="errors.link !== null">
                            <b-form-invalid-feedback v-for="(error, key) in errors.link" :key="'link_error_' + key" force-show>
                                {{ error }}
                            </b-form-invalid-feedback>
                        </template>

                    </b-form-group>
                </template>
                <template v-else>
                    <label class="d-block productLabel">Link(s)</label>

                    <template  v-for="link in productLinks">
                        <p class="productDetail"> <a :href="link" target="_blank" title="">{{ link }}</a></p>
                    </template>
                </template>

                <div class="row">
                    <div v-if="editMode" class="col-8">
                        <b-form-group label="Tuotekuva / Product picture" label-for="product-image">
                            <b-form-file id="product-image" v-model="file"
                                         drop-placeholder="Drop file here..."
                                         placeholder="Choose an image or drop it here..."
                                         type="image/*"
                                         accept="image/*"
                                         @input="onFileChanged"></b-form-file>

                        </b-form-group>
                    </div>
                    <div class="col-4">
                        <template v-if="editProduct.image">
                            <img :src="editProduct.image" class="img-fluid">

                            <b-button v-if="editMode" block type="button" variant="danger"
                                      @click="editProduct.image = null; file = null;">
                                <b-icon icon="trash-fill"></b-icon>
                                Remove image
                            </b-button>
                        </template>
                    </div>
                </div>


            </b-form>

            <template #modal-footer="{ cancel, ok }">
                <b-button @click="cancel">{{ editMode ? 'Cancel' : 'Close' }}</b-button>

                <b-button v-if="editMode" variant="primary"
                          @click="saveProduct()">
                    <template v-if="editProduct.id">Update</template>
                    <template v-else>Create</template>
                </b-button>
                <template v-else-if="companyId === editProduct.belongs_to_company && (isReseller || hasManagerPermission('products.edit'))">
                    <b-button
                        @click="editMode = true">Edit
                    </b-button>

                    <b-button
                        @click="confirmDeleteProduct">Delete
                    </b-button>
                </template>


            </template>
        </b-modal>


    </div>
</template>

<script>
import {cloneDeep, each} from 'lodash';
import '@css/settings-create-project.css';
import axios from "axios";

export default {
    props: {
        product: {
            type: Object,
            required: false,
            default: null
        },
        categories: {
            type: Array,
            required: true,
            default: []
        },
        isReseller: {
            type: Boolean,
            required: false,
            default: false
        }

    },
    data() {

        return {
            companyId: (typeof this.$store.state.company !== 'undefined' && typeof this.$store.state.company.id !== 'undefined') ? this.$store.state.company.id : null,
            editProduct: {
                id: null,
                name: null,
                manufacturer: null,
                product_category_id: null,
                description: null,
                image: null,
                variants: {
                    fields: ['CODE', 'COLOR', 'SIZE', 'PCS/BOX', 'EAN CODE'],
                    items: [{
                        'CODE': '',
                        'COLOR': '',
                        'SIZE': '',
                        'PCS/BOX': '',
                        'EAN CODE': ''
                    }]
                },
                belongs_to_company: this.companyId,
                link: null,
                eta_ce: null


            },
            editMode: true,
            errors: {
                name: null,
                manufacturer: null,
                'category.id': null,
                description: null,
                image: null,
                link: null,
                eta_ce: null
            },
            file: null,

        }
    },
    computed: {
        productLinks(){
          return this.editProduct.link ? this.editProduct.link.split(';') : []
        },
        hasErrors(argument) {
            let errors = false;

            each(this.errors, function (error) {
                if (error === false) {
                    errors = true;
                }
            });
            return errors;
        },
    },
    mounted() {
        this.$root.$on('bv::modal::shown', (bvEvent, modalId) => {
            if(modalId === 'product-modal'){
                if(this.product && this.product.id){
                    this.editProduct = cloneDeep(this.product);
                    if(!this.product.product_category_id && this.product.category){
                        this.editProduct.product_category_id = this.product.category.id;
                    }

                    this.editProduct.variants = this.editProduct.variants ? this.editProduct.variants : {
                        fields: ['CODE', 'COLOR', 'SIZE', 'PCS/BOX', 'EAN CODE'],
                        items: []
                    };
                }
                else{
                    this.resetProduct();
                }
            }

            this.editMode = !this.editProduct.id;

        });

    },
    methods: {
        updateProductVariantHeaderValue(headerIndex, value){

            if( this.editProduct.variants.fields.includes(value)){
                value += ' ' + headerIndex;
            }

            const oldValue = this.editProduct.variants.fields[headerIndex];

            this.editProduct.variants.items.forEach((variant, variantIndex) => {
                let variantFieldValue = '';
                if(variant[oldValue]){
                    variantFieldValue = cloneDeep(variant[oldValue]);
                    delete variant[oldValue];
                }

                this.editProduct.variants.items[variantIndex][value] = variantFieldValue;
            });

            this.editProduct.variants.fields[headerIndex] = value;

            this.$forceUpdate();
        },
        deleteProductVariantHeaderValue(headerIndex){
            const columnValue = this.editProduct.variants.fields[headerIndex];

            this.editProduct.variants.items.forEach((variant, variantIndex) => {
                delete variant[columnValue];
            });

            this.editProduct.variants.fields.splice(headerIndex, 1);
            this.$forceUpdate();
        },
        addProductVariantItem(){
            let variant = {};

            this.editProduct.variants.fields.forEach((field) => {
                variant[field] = '';
            });

            this.editProduct.variants.items.push(variant);
        },

        updateProductVariantItemValue(itemIndex, columnHeader, value){
            this.editProduct.variants.items[itemIndex][columnHeader] = value;
        },
        saveProduct() {
            const token = this.$store.state.token;
            let url, method;

            method = 'POST';

            if(this.isReseller){
                url = this.$store.state.apiURL + 'products';

                if(this.editProduct.id){
                    url += '/' + this.editProduct.id;
                    method = 'PUT';
                }
            }
            else{
                url = this.$store.getters.apiProjectBase + '/products';

            }



            axios({
                url: url,
                data: this.editProduct,
                method: method,
                headers: {"Authorization": "Bearer " + token}
            })
                .then(response => {
                    this.$bvModal.hide('product-modal');
                    this.$emit('productSaved');
                    this.editMode = false;
                })
                .catch((error, data) => {
                    const responseData = error.response.data;
                    console.log(responseData);
                    if (typeof responseData.errors !== 'undefined') {

                        for (const [key, error] of Object.entries(responseData.errors)) {
                            this.errors[key] = error;
                            console.log('setting', error, key);
                        }
                    } else if (typeof responseData.message !== 'undefined') {
                        alert(responseData.message);
                    } else {
                        alert('Something went wrong');
                    }
                });
        },

        deleteProduct(){
            let token = this.$store.state.token;

            let url;
            if(this.isReseller){
                url = this.$store.state.apiURL + 'products/' + this.editProduct.id;
            }
            else{
                url = this.$store.getters.apiProjectBase + '/products/' + this.editProduct.id;
            }

            let method = 'DELETE';

            axios({
                url: url,
                method: method,
                headers: {"Authorization": "Bearer " + token}
            })
                .then(response => {
                    this.$bvModal.hide('product-modal');
                    this.$emit('productSaved');
                    this.editMode = false;
                })
                .catch(error => {
                    alert('Something went wrong');
                    console.log(error);
                });
        },
        confirmDeleteProduct(){
            this.$bvModal.show('remove-product-modal');
        },

        onCancelProductCreation() {
            this.resetProduct();
            this.editMode = false;
        },
        resetProduct() {
            this.editProduct.id = null;
            this.editProduct.name = null;
            this.editProduct.manufacturer = null;
            this.editProduct.category = null;
            this.editProduct.description = null;
            this.editProduct.image = null;
            this.editProduct.link = null;
            this.editProduct.eta_ce = null;
            this.editProduct.belongs_to_company = this.companyId;
            this.editProduct.variants = {
                fields: ['CODE', 'COLOR', 'SIZE', 'PCS/BOX', 'EAN CODE'],
                items: []
            };
        },
        onFileChanged() {
            let reader = new FileReader();
            reader.addEventListener("load", () => {
                this.editProduct.image = reader.result;
                this.$forceUpdate();
            }, false);

            if (this.file) {
                reader.readAsDataURL(this.file);
            }
        }
    }
}
</script>

<style>


.variantsTable .table th, .variantsTable .table td {
    padding: 3px 5px;
}
.variantsTable .table th{
    color: #E20613
}
.variantsTable .table td{
    color: #212529;
}

.variantsTable .table td:last-of-type {
    text-align: left;
}

.productLabel{
    color: #E20613;
}
.productDetail{
    color: #212529;
}
table .btn.d-inline-block.btn-secondary.btn-small{
    background: none;
    box-sizing: border-box;
    margin-bottom: 2px;
    border: solid #fff 1px;
}
table .btn.d-inline-block.btn-secondary.btn-small:hover{
    background: #fff;
    border: solid #E20613 1px;
}

</style>
